import { TagMatch, TagMatchToggle } from 'components/TagMatchToggle'
import { useMemo, useState } from 'react'

/** Above this many tags a row gets a search box - below it, scanning the chips is faster. */
const SEARCH_THRESHOLD = 12

/**
 * Include/exclude tag scope for a run or report.
 *
 * The include row picks tags a loan must carry (`any` or `all`, via TagMatchToggle); the exclude
 * row picks tags that drop a loan outright. Exclusion always means "carries any of these", which
 * is why it has no any/all toggle of its own.
 *
 * A tag can only sit on one side — picking it on one row disables it on the other — so a
 * selection can never say "must have X" and "must not have X" at once.
 *
 * Shared because this chip picker was copy-pasted across every notice run/schedule dialog and the
 * statements screen; adding exclusion in one place keeps them behaving identically.
 *
 * Each row is capped and scrolls on its own. A converted book carries far more tags than the
 * handful this was designed around (Longhorn has ~100, one per investor/state/status), and two
 * unbounded chip walls grew the statements dialog past the viewport and pushed its buttons off
 * screen. The search box and the selected count keep a long list usable once it is boxed in.
 */
export function TagScopePicker({
  allTags,
  tags,
  onChangeTags,
  tagMatch,
  onChangeTagMatch,
  excludeTags,
  onChangeExcludeTags,
  includeHint = '(optional — only tagged loans)',
  className = '',
}: {
  allTags: string[]
  tags: string[]
  onChangeTags: (tags: string[]) => void
  tagMatch: TagMatch
  onChangeTagMatch: (value: TagMatch) => void
  excludeTags: string[]
  onChangeExcludeTags: (tags: string[]) => void
  includeHint?: string
  className?: string
}) {
  const [includeSearch, setIncludeSearch] = useState('')
  const [excludeSearch, setExcludeSearch] = useState('')

  const toggle = (list: string[], onChange: (next: string[]) => void, tag: string) =>
    onChange(list.includes(tag) ? list.filter((t) => t !== tag) : [...list, tag])

  const searchable = allTags.length > SEARCH_THRESHOLD
  const filtered = useMemo(() => {
    const match = (needle: string) => {
      const value = needle.trim().toLowerCase()
      return value ? allTags.filter((t) => t.toLowerCase().includes(value)) : allTags
    }
    return { include: match(includeSearch), exclude: match(excludeSearch) }
  }, [allTags, includeSearch, excludeSearch])

  if (!allTags.length) {
    return (
      <div className={`text-sm text-gray-600 ${className}`}>
        Filter by Tag <span className="text-gray-400">{includeHint}</span>
        <p className="mt-1 text-xs text-gray-400">No tags in use yet.</p>
      </div>
    )
  }

  /** Header line: the label, its hint, and - once something is picked - a count and a reset. */
  const heading = (
    label: string,
    hint: string,
    selected: string[],
    onChange: (next: string[]) => void,
  ) => (
    <div className="flex flex-wrap items-center gap-x-2">
      <span>
        {label} <span className="text-gray-400">{hint}</span>
      </span>
      {selected.length > 0 && (
        <span className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          {selected.length} selected
          <button type="button" className="text-shade-blue hover:underline" onClick={() => onChange([])}>
            Clear
          </button>
        </span>
      )}
    </div>
  )

  const row = (
    selected: string[],
    otherSide: string[],
    onChange: (next: string[]) => void,
    variant: 'include' | 'exclude',
    shown: string[],
    search: string,
    onChangeSearch: (value: string) => void,
  ) => (
    <>
      {searchable && (
        <input
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-xs"
          placeholder={`Search ${allTags.length} tags`}
          value={search}
          onChange={(e) => onChangeSearch(e.target.value)}
        />
      )}
      <div className="mt-1 flex max-h-[7.5rem] flex-wrap content-start gap-1.5 overflow-y-auto rounded border border-gray-200 p-1.5">
        {shown.map((t) => {
          const on = selected.includes(t)
          const blocked = !on && otherSide.includes(t)
          const onStyle =
            variant === 'include' ? 'border-shade-blue bg-shade-blue text-white' : 'border-red-500 bg-red-500 text-white'
          const offStyle =
            variant === 'include'
              ? 'border-gray-300 bg-white text-gray-600 hover:border-shade-blue'
              : 'border-gray-300 bg-white text-gray-600 hover:border-red-500'
          return (
            <button
              key={t}
              type="button"
              disabled={blocked}
              title={blocked ? 'Already used on the other side of this filter' : undefined}
              onClick={() => toggle(selected, onChange, t)}
              className={`h-fit rounded-full border px-2 py-0.5 text-xs ${
                blocked ? 'cursor-not-allowed border-gray-200 bg-white text-gray-300' : on ? onStyle : offStyle
              }`}
            >
              {t}
            </button>
          )
        })}
        {shown.length === 0 && <span className="px-1 text-xs text-gray-400">No tag matches “{search}”.</span>}
      </div>
    </>
  )

  return (
    <div className={className}>
      <div className="text-sm text-gray-600">
        {heading('Filter by Tag', includeHint, tags, onChangeTags)}
        {row(tags, excludeTags, onChangeTags, 'include', filtered.include, includeSearch, setIncludeSearch)}
        <TagMatchToggle className="mt-2" value={tagMatch} tagCount={tags.length} onChange={onChangeTagMatch} />
      </div>
      <div className="mt-3 text-sm text-gray-600">
        {heading(
          'Exclude Tags',
          '(optional: skips loans carrying any of these)',
          excludeTags,
          onChangeExcludeTags,
        )}
        {row(excludeTags, tags, onChangeExcludeTags, 'exclude', filtered.exclude, excludeSearch, setExcludeSearch)}
      </div>
    </div>
  )
}
